import React, { createContext, useState, useMemo } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

export const ThemeContext = createContext({ toggleColorMode: () => {} });

const AppThemeProvider = ({ children }) => {
  const [mode, setMode] = useState('light'); // Changed default to light mode

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [],
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === 'light' ? '#1976d2' : '#1a2a3a',
          },
          secondary: {
            main: mode === 'light' ? '#dc004e' : '#4fc3f7',
          },
          error: {
            main: '#ff6f61',
          },
          background: {
            default: mode === 'light' ? '#ffffff' : '#121c27',
            paper: mode === 'light' ? '#f5f5f5' : '#1a2a3a',
          },
          text: {
            primary: mode === 'light' ? '#212121' : '#ffffff',
            secondary: mode === 'light' ? '#757575' : '#bbbbbb',
          },
        },
        typography: {
          fontFamily: 'Roboto, Arial, sans-serif',
          h1: { fontSize: '3.5rem' },
          h2: { fontSize: '3rem' },
          h3: { fontSize: '2.5rem' },
          h4: { fontSize: '2rem' },
          h5: { fontSize: '1.5rem' },
          h6: { fontSize: '1.25rem' },
        },
        components: {
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#1976d2' : '#1a2a3a',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
              },
              containedSecondary: {
                backgroundColor: mode === 'light' ? '#dc004e' : '#4fc3f7',
                color: mode === 'light' ? '#ffffff' : '#121c27',
                '&:hover': {
                  backgroundColor: mode === 'light' ? '#ff3d72' : '#81d4fa',
                },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#ffffff' : '#1a2a3a',
                color: mode === 'light' ? '#212121' : '#ffffff',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#ffffff' : '#1a2a3a',
                color: mode === 'light' ? '#212121' : '#ffffff',
              },
            },
          },
        },
      }),
    [mode],
  );

  return (
    <ThemeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default AppThemeProvider; 
